<template>
  <div class="flex flex-col gap-5">
    <!--  Logistic Details Card  -->
    <div class="h-full logistic-container">
      <div class="w-1/3 logistic-container">
        <table
          v-if="costBreakdownTableData.length"
          class="w-full h-full border-collapse"
        >
          <thead>
            <tr>
              <th class="text-left">
                ITM
              </th>
              <th>
                Budget
              </th>
              <th>
                Outlook
              </th>
            </tr>
          </thead>
          <tbody
            ref="totalLogisticCostTable"
          >
            <tr
              v-for="(row) of costBreakdownTableData"
              :key="row.item_name"
              :name="row.item_name"
              @click="onClickRow(row.item_name)"
              class="cursor-pointer"
            >
              <td
                v-if="row.isChild"
                class="pl-3"
                :class="{
                  'font-semibold': row.isTotal
                }"
              >
                &#x2022; {{ row.item_name }}
              </td>
              <td
                v-else
                :class="{
                  'font-semibold': row.isTotal
                }"
              >
                {{ row.item_name }}
              </td>
              <td class="text-right">
                {{ formatNumber(row.budget) }}
              </td>
              <td class="text-right">
                {{ formatNumber(row.outlook) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="w-2/3 self-start flex flex-col gap-3">
        <span class="text-xl font-semibold">
          {{ currentRowName }}
        </span>
        <div
          v-if="!isEmpty(currentCompaniesData)"
          class="grid grid-cols-3 gap-3"
        >
          <div
            v-for="(row) of currentCompaniesData"
            :key="row.company_name"
            class="p-3 flex flex-col gap-3 bg-white rounded-lg shadow-md"
          >
            <div
              class="flex flex-row items-center justify-around company-data-container"
              :class="{
                'green': row.percentage <= 0,
                'red': row.percentage > 0
              }"
            >
              <span class="text-3xl font-semibold">
                {{ row.company_name }}
              </span>
              <div class="flex flex-col items-center justify-between gap-1">
                <vs-icon
                  :icon="row.percentage < 0 ? 'trending_down' : 'trending_up'"
                  color="#FFF"
                  size="medium"
                />
                <span class="text-xl font-semibold">
                  {{
                    row.percentage == null
                      ? 'N/A'
                      : handleValidateAndFormatNumber(row.percentage)
                  }}%
                </span>
              </div>
            </div>
            <div class="flex flex-row items-center justify-around">
              <div class="flex flex-col items-center justify-between gap-2">
                <span>Budget</span>
                <span class="text-3xl font-semibold">
                  {{ handleValidateAndFormatNumber(row.budget) }}
                </span>
              </div>
              <div class="flex flex-col items-center justify-between gap-2">
                <span>Outlook</span>
                <span class="text-3xl font-semibold">
                  {{ handleValidateAndFormatNumber(row.outlook) }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div v-else>
          No Data
        </div>
      </div>
    </div>

    <!--  Barge Cycle Days Card  -->
    <div class="h-full w-full p-5 flex flex-col gap-3 bg-white rounded-lg shadow-md">
      <div class="pb-3 flex flex-row items-center justify-between border-b-2 border-dashed">
        <span class="text-xl font-semibold">
          Barge Cycle Days
        </span>
        <div class="flex flex-row items-center justify-between gap-2">
          <select-with-search
            v-model="filter.site"
            class="w-64"
            placeholder="Select site"
            :options="options.site"
          />
          <select-with-search
            v-model="filter.pol"
            class="w-64"
            placeholder="Select POL"
            :options="options.pol"
          />
          <select-with-search
            v-model="filter.pod"
            class="w-64"
            placeholder="Select POD"
            :options="options.pod"
          />
        </div>
      </div>
      <div
        class="w-full flex flex-row items-center"
        v-if="barChartData && lineChartData"
      >
        <div class="w-1/3 h-full">
          <bar-chart
            v-if="barChartData"
            class="w-full h-full"
            :data="barChartData"
            :options="horizontalBarChartOptions"
            :plugins="horizontalBarChartPlugins"
            :height="horizontalBarChartHeight"
            :key="horizontalBarChartHeight"
          />
        </div>
        <div class="w-2/3 flex flex-col gap-3">
          <div
            v-for="(chartData, index) of lineChartData"
            :key="index"
            class="h-24"
          >
            <line-chart
              v-if="chartData"
              class="h-full"
              :data="chartData"
              :options="{
                ...lineChartOptions,
                ...((index === lineChartData.length - 1) && lineChartTicksOption)
              }"
              :plugins="[
                ...lineChartPlugins,
                index === lineChartData.length - 1 && roundedTickPlugin()
              ]"
              :key="lineChartData.length"
            />
          </div>
        </div>
      </div>
      <div
        v-else
        class="h-16 flex items-center justify-center"
      >
        <span class="text-grey-001"> No data found </span>
      </div>
    </div>
  </div>
</template>
  
<script>
import {cloneDeep, find, isEmpty, isNumber, slice} from "lodash";
import {mapGetters} from "vuex";

import BarChart from "@/components/barChart/index.vue";
import LineChart from "@/components/lineChart/index.vue";
import SelectWithSearch from "@/components/selectWithSearch/index.vue";

export default {
  name: 'TotalLogisticCost',
  props: {
    filters: {
      type: Object,
      default: () => ({})
    }
  },
  components: {
    SelectWithSearch,
    BarChart,
    LineChart
  },
  data() {
    return {
      costBreakdownTableData: [],
      currentRowName: 'Total Expenses',
      currentCompaniesData: [],

      barChartData: null,
      lineChartData: [],
      filter: {
        site: null,
        pol: null,
        pod: null
      },
      options: {
        site: [],
        pol: [],
        pod: []
      }
    }
  },
  mounted() {
    this.fetchTotalLogisticCost()
    this.fetchCompany()
    this.fetchPodItems()
    this.fetchPolItems()
  },
  watch: {
    totalLogisticCost: {
      deep: true,
      handler (val) {
        if (val) {
          const costBreakdown = cloneDeep(val?.itm_cost_spec)
          this.costBreakdownTableData = costBreakdown
            ?.sort((a, b) => a?.order - b?.order )
            ?.reduce((arr, item) => {
              if (item?.item_name?.toLowerCase()?.includes('total')) {
                item['isTotal'] = true
              }
              arr.push(item)

              if (item?.children?.length) {
                const details = item.children
                  ?.sort((a, b) => a.order - b.order)
                  ?.map((detail) => ({
                    ...detail,
                    isChild: true,
                    ...(detail.item_name?.toLowerCase()?.includes('total') && { isTotal: true })
                  }))
                arr.push(...details)
              }

              return arr
            }, [])

          this.setHighlight()

          this.currentCompaniesData = costBreakdown.find((item) => item?.item_name === this.currentRowName)?.detail_per_companies

          if (isEmpty(val?.barge_cycle_days?.grouped_by_route)) {
            this.barChartData = null
            this.lineChartData = null
            return
          }

          this.barChartData = this.processBarChartData(val?.barge_cycle_days?.grouped_by_route)
          const mappedPtd = val?.barge_cycle_days?.grouped_by_route?.map((item) =>({
            label: item?.label,
            route: item?.route,
            grouped_by_month: item?.grouped_by_month
          }))
          this.lineChartData = this.processLineChartData(mappedPtd)
        }
      }
    },
    currentRowName: {
      handler() {
        this.setHighlight()
      }
    },
    companies: {
      deep: true,
      immediate: true,
      handler(val) {
        if(val) {
          this.options.site = val.map(item => ({
            text: item.code,
            value: item.code
          }))
        }
      }
    },
    polItems: {
      deep: true,
      immediate: true,
      handler(val) {
        if(val) {
          this.options.pol = val.map(item => ({
            text: item.name,
            value: item.name
          }))
        }
      }
    },
    podItems: {
      deep: true,
      immediate: true,
      handler(val) {
        if(val) {
          this.options.pod = val.map(item => ({
            text: item.name,
            value: item.name
          }))
        }
      }
    },
    filters: {
      deep: true,
      handler() {
        this.fetchTotalLogisticCost()
      }
    },
    filter: {
      deep: true,
      handler() {
        this.fetchTotalLogisticCost()
      }
    }
  },
  computed: {
    ...mapGetters({
      totalLogisticCost: 'CostAnalysis/totalLogisticCost',
      routes: 'BargeCostUtilization/routes',
      companies: 'Companies/companies',
      polItems: 'BargeLineUp/polItems',
      podItems: 'BargeLineUp/podItems'
    }),
    horizontalBarChartHeight() {
      const dataLength = this.totalLogisticCost?.barge_cycle_days?.grouped_by_route?.length
      const baseHeight = 100
      const barThickness = 40
      const padding = dataLength < 5 ? 20 : 25

      return Math.min(400, baseHeight + (dataLength * barThickness) + padding)
    },
    horizontalBarChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        barThickness: 40,
        layout: {
          padding: {
            right: 50
          }
        },
        indexAxis: 'y',
        parsing: {
          xAxisKey: 'y',
          yAxisKey: 'x'
        },
        scales: {
          x: {
            grid: {
              display: false,
              drawBorder: false
            },
            ticks: {
              display: false,
              stepSize: 1
            },
            title: {
              display: true,
              align: 'center',
              text: this.moment().format('YYYY'),
              font: { size: 12, family: 'Poppins' },
              color: 'rgba(0, 0, 0, 1)'
            }
          },
          y: {
            grid: {
              display: false,
              drawBorder: false
            },
            ticks: {
              display: false
            }
          }
        },
        plugins: {
          legend: {
            display: false
          },
          chartAreaBorder: {
            borderColor: 'black',
            borderWidth: 2
          },
          tooltip: {
            enabled: true,
            interaction: {
              mode: 'y',
              intersect: false
            },
            backgroundColor: '#FAFDFF',
            titleColor: '#000000',
            titleFont: {
              weight: 'Bold',
              family: 'Poppins'
            },
            titleAlign: 'center',
            bodyColor: '#000000',
            bodyFont: {
              family: 'Poppins'
            },
            bodyAlign: 'center',
            borderWidth: 1,
            cornerRadius: 6,
            displayColors: false,
            callbacks: {
              title(tooltipItem) {
                return `${tooltipItem?.[0]?.label}`
              },
              label(tooltipItem) {
                return [
                  `Actual: ${tooltipItem?.raw?.y?.toFixed(2)} days`,
                  `Outlook: ${tooltipItem?.raw?.outlook?.toFixed(2)} days`,
                  `Budget: ${tooltipItem?.raw?.budget?.toFixed(2)} days`
                ]
              }
            }
          }
        }
      }
    },
    horizontalBarChartPlugins() {
      return [
        this.baselinePlugin(),
        this.gradientFillPlugin(),
        this.chartAreaBorderPlugin(),
        this.drawBarLabelPlugin()
      ];
    },
    lineChartOptions() {
      return {
        responsive: true,
        stacked: false,
        maintainAspectRatio: false,
        scales: {
          x: {
            display: false,
            offset: true
          },
          y: {
            type: 'linear',
            display: false,
            offset: true,
            grid: {
              drawOnChartArea: true
            }
          }
        },
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            enabled: true,
            backgroundColor: '#FAFDFF',
            titleColor: '#000000',
            titleFont: {
              weight: 'Bold',
              family: 'Poppins'
            },
            titleAlign: 'center',
            bodyColor: '#000000',
            bodyFont: {
              family: 'Poppins'
            },
            bodyAlign: 'center',
            borderWidth: 1,
            cornerRadius: 6,
            displayColors: false,
            callbacks: {
              title(tooltipItem) {
                return `${tooltipItem?.[0]?.label}`
              },
              label(tooltipItem) {
                const key = tooltipItem?.raw?.key || 'Value'
                const y = tooltipItem?.raw?.y
                const budget = tooltipItem?.raw?.budget

                const primaryLine = `${key}: ${isNumber(y) ? y.toFixed(2) : '0.00'} days`
                const budgetLine = `Budget: ${isNumber(budget) ? budget.toFixed(2) : '0.00'} days`

                return [primaryLine, budgetLine]
              }
            }
          },
          customFill: {
            nextMonth: parseInt(this.moment().format('M'))
          }
        }
      }
    },
    lineChartTicksOption() {
      return {
        layout: {
          padding: {
            bottom: 5
          }
        },
        scales: {
          x: {
            ticks: {
              display: true,
              font: { size: 12, family: 'Poppins' },
              color: 'rgba(0, 0, 0, 1)',
              padding: 20
            },
            grid: {
              display: true,
              drawOnChartArea: false,
              drawBorder: false,
              drawTicks: true
            },
            offset: true
          },
          y: {
            display: false,
            ticks: {
              display: false
            },
            grid: {
              display: false
            },
            offset: true
          }
        }
      }
    },
    lineChartPlugins() {
      return [
        this.customFillPlugin()
      ]
    }
  },
  methods: {
    async fetchTotalLogisticCost() {
      const filters = {
        ...this.filters,
        barge_cycle_days_site: this.filter.site,
        barge_cycle_days_pol: this.filter.pol,
        barge_cycle_days_pod: this.filter.pod
      }
      try {
        await this.$store.dispatch('CostAnalysis/fetchTotalLogisticCost', filters);
      } catch (err) {
        throw err;
      }
    },
    async fetchCompany() {
      try {
        await this.$store.dispatch('Companies/fetchCompanies', {
          is_master_data: true,
          type: 'SITE_OWNER',
          status: true,
          sort_dir: 'asc',
          sort_by: 'name',
          limit:null,
          page: null
        })
      } catch (e) {
        this.showFailedNotification(this, 'Failed to fetch companies, please try again')
      }
    },
    async fetchPodItems() {
      await this.$store.dispatch('BargeLineUp/getDropdownOptionsBLU', {type: 'POD'})
    },
    async fetchPolItems() {
      await this.$store.dispatch('BargeLineUp/getDropdownOptionsBLU', { type: 'POL'})
    },
    setHighlight() {
      this.$nextTick(() => {
        const highlightedRow = document.getElementsByClassName('green-highlight')
        if (highlightedRow.length) {
          highlightedRow[0].classList.remove('green-highlight')
        }
        
        const chosenRow = document.getElementsByName(this.currentRowName)
        if (chosenRow.length) {
          chosenRow[0].classList.add('green-highlight')
        }
      })
    },
    onClickRow(rowName) {
      this.currentRowName = rowName;

      this.currentCompaniesData = this.costBreakdownTableData.find((item) => item?.item_name === rowName)?.detail_per_companies
    },
    formatNumber(number) {
      return number?.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }) || 'N/A'
    },
    processBarChartData(data) {
      return {
        labels: data.map((item) => item?.label),
        datasets: [
          {
            data: data.map((item) => ({
              x: item?.label,
              y: item?.actual,
              outlook: item?.outlook,
              budget: item?.budget
            })),
            borderRadius: 12
          }
        ]
      }
    },
    processLineChartData(data) {
      const months = Array.from({ length: 12 }, (_, i) =>
        this.moment().month(i).format('MMM')
      );

      const currentMonthIndex = parseInt(this.moment().format('M'))

      return data.map((item) => {
        const monthlyData = Array.from({ length: 12 }, (_, i) => {
          const monthNumber = parseInt(this.moment().month(i).format('M'),10)
          const related = find(item.grouped_by_month, { month: monthNumber })

          const value =
            i + 1 <= currentMonthIndex ? related?.actual || 0 : related?.outlook || 0

          return {
            x: i + 1,
            y: value,
            key: i + 1 <= currentMonthIndex ? 'Actual' : 'Outlook',
            // used only by tooltip (does not affect plotted y value)
            budget: related?.budget
          }
        })

        return {
          labels: months,
          datasets: [
            {
              label: item.routes,
              data: monthlyData,
              borderColor: '#000',
              backgroundColor: '#000',
              pointRadius: 4,
              borderWidth: 2
            }
          ]
        }
      })
    },
    baselinePlugin() {
      return {
        id: 'baselinePlugin',
        afterDatasetsDraw(chart) {
          const { ctx, scales, chartArea } = chart;
          const xScale = scales.x
          const yScale = scales.y
          const meta = chart.getDatasetMeta(0)
          const data = chart.data.datasets[0].data
          const padding = 5

          ctx.save()
          ctx.lineWidth = 1
          ctx.strokeStyle = '#FF0000'

          meta.data.forEach((bar, i) => {
            const model = bar
            const barHeight = model.height
            const y = model.y

            const baselineX = xScale.getPixelForValue(data[i]?.outlook)

            const barTop = y - barHeight / 2
            const barBottom = y + barHeight / 2

            ctx.beginPath()
            ctx.moveTo(baselineX, barTop - padding)
            ctx.lineTo(baselineX, barBottom + padding)
            ctx.stroke()
          })

          ctx.restore()
        }
      }
    },
    gradientFillPlugin() {
      return {
        id: 'gradientFillPlugin',
        afterDatasetDraw(chart) {
          const { ctx } = chart
          chart.data.datasets.forEach((dataset, i) => {
            const meta = chart.getDatasetMeta(i)
            meta.data.forEach((bar) => {
              const { x, y, base, height } = bar
              const width = x - base
              const barHeight = height;

              const gradient = ctx.createLinearGradient(base, y, x, y)
              gradient.addColorStop(0, '#428AF5')
              gradient.addColorStop(1, '#9BD8F7')

              const radius = 5
              const top = y - barHeight / 2
              const bottom = y + barHeight / 2

              ctx.beginPath()
              ctx.moveTo(base + radius, top)
              ctx.lineTo(base + width - radius, top)
              ctx.quadraticCurveTo(base + width, top, base + width, top + radius)
              ctx.lineTo(base + width, bottom - radius)
              ctx.quadraticCurveTo(base + width, bottom, base + width - radius, bottom)
              ctx.lineTo(base + radius, bottom)
              ctx.quadraticCurveTo(base, bottom, base, bottom - radius)
              ctx.lineTo(base, top + radius)
              ctx.quadraticCurveTo(base, top, base + radius, top)
              ctx.closePath()

              ctx.fillStyle = gradient
              ctx.fill();
            })
          })

          return false
        }
      }
    },
    customFillPlugin() {
      return {
        id: 'customFill',
        beforeDraw(chart, args, options) {
          const { ctx, chartArea: { left, right, top, bottom, height }, scales: { x, y } } = chart
          const meta = chart.getDatasetMeta(0)
          const points = meta.data
          let firstIndex = options?.nextMonth
          if(firstIndex === 12) firstIndex = 1
          const pointDistance = ((right - left) / (meta.data.length - 1)) / 2
          const yellowFillStartX = points[firstIndex].x - pointDistance

          const padding = isNumber(chart?.options?.layout?.padding)
            ? chart?.options?.layout?.padding
            : 0

          ctx.fillStyle = '#FDF4BA'
          ctx.fillRect(
            yellowFillStartX,
            0,
            x.right - yellowFillStartX,
            height + padding
          )
        }
      }
    },
    chartAreaBorderPlugin() {
      return {
        id: 'chartAreaBorder',
        beforeDraw(chart, args, options) {
          const {ctx, chartArea: {left, top, width, height}} = chart

          ctx.save();
          ctx.strokeStyle = options.borderColor || 'black'
          ctx.lineWidth = options.borderWidth || 2

          const dataLength = chart?.data?.datasets[0]?.data.length || 1
          const fixedHeight = height / dataLength / 6

          ctx.beginPath();
          ctx.moveTo(left, top + fixedHeight)
          ctx.lineTo(left, top + height - fixedHeight)
          ctx.stroke()

          ctx.beginPath()
          ctx.moveTo(left, top + height - fixedHeight)
          ctx.lineTo(left + width, top + height - fixedHeight)
          ctx.stroke();

          ctx.restore()
        }
      }
    },
    roundedTickPlugin() {
      return {
        id: 'roundedTicks',
        beforeDraw(chart) {
          const { ctx, chartArea, scales } = chart;
          const xScale = scales.x;
          const yStart = chartArea.bottom;

          ctx.save();
          ctx.fillStyle = 'rgba(0, 0, 0, 1)';

          xScale.ticks.forEach((tick, i) => {
            const x = xScale.getPixelForTick(i);
            ctx.beginPath();
            ctx.arc(x, yStart + 5, 3, 0, Math.PI * 2); // y + 5 to go below the axis line
            ctx.fill();
          });

          ctx.restore();
        }
      }
    },
    drawBarLabelPlugin() {
      return {
        id: 'drawBarLabel',
        afterDatasetDraw(chart) {
          const {ctx, chartArea: {width, height}, data} = chart
          const meta = chart.getDatasetMeta(0)
          const padding = 10

          meta.data.forEach((arc, index) => {
            const value = data.datasets[0].data[index].x
            const {x, y, width, height, base} = arc

            ctx.save()
            ctx.fillStyle = '#000'
            ctx.font = '600 11px Poppins'
            ctx.textAlign = 'left'
            ctx.textBaseline = 'middle'
            ctx.fillText(value, base + padding, y)
            ctx.restore()
          });
        }
      }
    }
  }
};
</script>
  
<style lang="scss" scoped>
table {
  thead {
    th {
      @apply p-2 text-center text-base;
      border-bottom: 2px solid #D7D2E4;
    }
  }

  tbody {
    tr {
      td {
        @apply p-2;
        border-bottom: 2px solid #D7D2E4;
      }
    }
  }

  .green-highlight {
    background: #ADFFDC;
  }
}

.logistic-container {
  @apply w-full p-5 flex flex-row items-center gap-5 bg-white rounded-lg shadow-md;
}

.company-data-container {
  @apply p-2 text-white rounded-md;

  &.green {
    background: #33B57D;
  }

  &.red {
    background: #FF3662;
  }
}
</style> 